import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const PARAM_NAME = 'campaignPerformance';

const storageKey = (userId?: string): string => `arcdp:flags:${userId ?? 'default'}:campaignPerformanceEnabled`;

const readFlag = (userId?: string): boolean => {
  try {
    return localStorage.getItem(storageKey(userId)) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (enabled: boolean, userId?: string) => {
  try {
    if (enabled) localStorage.setItem(storageKey(userId), '1');
    else localStorage.removeItem(storageKey(userId));
  } catch {
    /* ignore */
  }
};

const TRUE_VALUES = new Set(['1', 'true', 'enable', 'on']);
const FALSE_VALUES = new Set(['0', 'false', 'disable', 'off']);

/**
 * Gates the Campaign Performance feature behind a URL parameter that the user must
 * pass once to unlock. Subsequent visits remember the choice in localStorage,
 * scoped to the current Auth0 user.
 *
 * Activate:   any URL with `?campaignPerformance=1`
 * Deactivate: any URL with `?campaignPerformance=0`
 */
export const useCampaignPerformanceEnabled = (): boolean => {
  const { user } = useAuth0();
  const location = useLocation();
  const history = useHistory();
  const [enabled, setEnabled] = useState<boolean>(() => readFlag(user?.sub));

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const value = sp.get(PARAM_NAME);

    if (value !== null) {
      if (TRUE_VALUES.has(value)) {
        writeFlag(true, user?.sub);
        setEnabled(true);
      } else if (FALSE_VALUES.has(value)) {
        writeFlag(false, user?.sub);
        setEnabled(false);
      }
      sp.delete(PARAM_NAME);
      const nextSearch = sp.toString();
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      });
      return;
    }

    setEnabled(readFlag(user?.sub));
  }, [location.search, location.pathname, user?.sub]);

  return enabled;
};
